import { useCallback } from 'react';
import type {
  ConfigEntityDetail,
  EntityDetailSchema,
} from '@web-config-app/core';
import { pipeFunctions } from '@web-config-app/core';
import { useEntities } from '../use-entities/use-entities';
import { injectDomainEntityId } from '../../utilities/inject-domain-entity-id/inject-domain-entity-id';
import { transposeIdToSourceId } from '../../utilities/transpose-id-to-source-id/transpose-id-to-source-id';
import { removeDomainEntityId } from '../../utilities/remove-domain-entity-id/remove-domain-entity-id';
import { regenerateGeneratedIds } from '../../utilities/regenerate-generated-ids/regenerate-generated-ids';

/**
 * Hook that returns a callback for processing included entities during deep clone operations.
 *
 * This hook accesses the entity configurations from the application context and provides
 * a callback function that handles domain entity ID injection and ID transposition.
 *
 * @returns A callback function that processes entity details for cloning
 */
export const useProcessIncludedEntity = () => {
  const allEntities = useEntities();

  /**
   * Processes an included entity for deep cloning by:
   * 1. Removing existing domain entity ID if present
   * 2. Injecting a domain entity ID if applicable
   * 3. Transposing the entity ID to sourceId
   *
   * @param entityDetail - The entity detail to process
   * @returns The processed entity with ID transposed and domain entity ID injected if applicable
   */
  const processIncludedEntity = useCallback(
    (entityDetail: ConfigEntityDetail, schema: EntityDetailSchema) => {
      const { domainEntityType } = entityDetail.attributes.entityMetadata;

      // Find the entity configuration based on domain entity type
      const foundEntity = domainEntityType
        ? allEntities.get(domainEntityType)
        : undefined;

      // create a single function to process the data that will be used to remove the domain entity ID
      const processData = pipeFunctions(
        regenerateGeneratedIds,
        removeDomainEntityId,
      );

      const processedEntityData = processData(entityDetail, schema);

      // If domain entity ID generation is enabled, inject a new ID
      if (foundEntity?.generateDomainEntityId?.enabled) {
        return transposeIdToSourceId(
          injectDomainEntityId(
            foundEntity.generateDomainEntityId,
            processedEntityData,
          ) as ConfigEntityDetail,
        );
      }

      // Otherwise just transpose the ID
      return transposeIdToSourceId(processedEntityData);
    },
    [allEntities],
  );

  return processIncludedEntity;
};
